import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxBorder, BoxRadius, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Box } from 'native-base';
import React from 'react';

import {
  TableContent,
  TableContentItemText,
} from '../../../../../../../../../components/TableContent';
import { useGlobalAssetContext } from '../../../../../contexts';
import { useEquityAssetController } from '../../../../../hooks/products/Equity';
import { AssetTitleWithImage } from '../../../components/AssetTitleWithImage';
import { AssetValueWithPercentage } from '../../../components/AssetValueWithPercentage';
import { LateralBar } from '../../../components/LateralBar';
import { ListLoadingItemDesktop } from '../ListLoadingItem/desktop';

import { EQUITY_ORDER_TYPE_LABEL, EQUITY_ORDER_TYPE_OPTIONS, EquityListItemTypes } from '.';

export const EquityListItemDesktop: React.FC<EquityListItemTypes.Props> = ({ offer }) => {
  const id = offer.quote.productId;
  const {
    amountItem,
    priceItem,
    orderType,
    handleChangeOrderType,
    totalPrice,
    handleChangeAmountItem,
    handleChangePriceItem,
    totalPriceIsInvalid,
  } = useEquityAssetController(offer);
  const { infoGetter, selectedTab, isBatchTicket } = useGlobalAssetContext();

  const completeOffer = {
    ...offer,
    orderQuantity: amountItem,
    orderValue: priceItem,
    orderType,
  };

  const { Button, isSelected, forceLoading } = infoGetter(
    'equity',
    completeOffer,
    'productIdNormalized'
  );

  if (forceLoading) {
    return <ListLoadingItemDesktop />;
  }

  return (
    <TableContent
      direction="column"
      space={Spacing['stack.xxxs']}
      p={0}
      px={Spacing['inline.xxxs']}
      py={Spacing['stack.xxxs']}
      isHighlighted={isSelected}>
      <LateralBar
        backgroundColor={selectedTab.color}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <HStack justifyContent="space-between" w="full">
        <Stack w="1/5">
          <AssetTitleWithImage
            image={offer.imageNormalized || ''}
            title={offer.productNameNormalized || ''}
            subtitle={offer.symbolNormalized || ''}
          />
        </Stack>
        <Stack w="1/5">
          <VStack space={Spacing['stack.quarck']} justifyContent="space-between">
            <UI.Text color="neutral.black" variant="semibold">
              Ultimo preço
            </UI.Text>
            <AssetValueWithPercentage
              value={offer.lastPriceNormalized}
              percentage={offer.priceChangeTaxValueNormalized}
              iconColor={offer.priceChangeTaxColorNormalized}
              updateDate={offer.tradeDateNormalized}
            />
          </VStack>
        </Stack>
        <Stack w="1/5">
          <VStack space={Spacing['stack.quarck']} justifyContent="space-between">
            <UI.Text color="neutral.black" variant="semibold">
              Melhor Compra
            </UI.Text>
            <TableContentItemText>{offer.bidPriceNormalized}</TableContentItemText>
          </VStack>
        </Stack>
        <Stack w="1/5">
          <VStack space={Spacing['stack.quarck']} justifyContent="space-between">
            <UI.Text color="neutral.black" variant="semibold">
              Melhor Venda
            </UI.Text>
            <TableContentItemText>{offer.askPriceNormalized}</TableContentItemText>
          </VStack>
        </Stack>
        <Stack w="1/5">
          <VStack space={Spacing['stack.quarck']} justifyContent="space-between">
            <UI.Text color="neutral.black" variant="semibold">
              Fechamento
            </UI.Text>
            <TableContentItemText>{offer.closePriceNormalized}</TableContentItemText>
          </VStack>
        </Stack>
      </HStack>
      <HStack justifyContent="space-between" w="full" alignItems="center">
        <Stack w="1/5">
          <VStack space={Spacing['stack.quarck']} justifyContent="space-between">
            <UI.Text color="neutral.black" variant="semibold">
              Tipo da ordem
            </UI.Text>
            {isBatchTicket ? (
              <TableContentItemText>Limitada</TableContentItemText>
            ) : (
              <Stack
                mr={Spacing['inline.xxxs']}
                borderRadius={BoxBorder.thick}
                bgColor="backgroundshade">
                <UI.Select
                  testID={`equity-order-type-select${id}`}
                  options={EQUITY_ORDER_TYPE_OPTIONS}
                  selectedValue={orderType}
                  disabled={isSelected}
                  onValueChange={(value) => {
                    handleChangeOrderType(value as EquityListItemTypes.OrderType);
                  }}
                  placeholder={EQUITY_ORDER_TYPE_LABEL.Limit}
                />
              </Stack>
            )}
          </VStack>
        </Stack>
        <Stack w="1/5">
          <VStack space={Spacing['stack.quarck']} justifyContent="space-between">
            <UI.Text color="neutral.black" variant="semibold">
              Quantidade
            </UI.Text>
            <Stack pr={Spacing['inline.xxxs']}>
              <UI.InputStepper
                stepperValue={offer.quote.lotSize || 1}
                max={1000000}
                min={offer.quote.lotSize || 1}
                name={`equity-amount-order-input${id}`}
                testId={`equity-amount-order-input${id}`}
                keyboardType="numeric"
                fontSize={Typography['fontsize.md']}
                defaultValue={amountItem}
                onChange={handleChangeAmountItem}
                showErrorMessage={false}
                disabled={isSelected}
                {...((totalPrice === 0 || totalPrice > 1000000) && {
                  customError: 'Quantidade inválida',
                })}
              />
            </Stack>
          </VStack>
        </Stack>
        <Stack w="1/5">
          <VStack space={Spacing['stack.quarck']} justifyContent="space-between">
            <UI.Text color="neutral.black" variant="semibold">
              Preço por ativo
            </UI.Text>
            <Stack pr={Spacing['inline.xxxs']}>
              <UI.InputStepper
                stepperValue={0.01}
                max={1000000}
                min={0.01}
                fontSize={Typography['fontsize.md']}
                keyboardType="numeric"
                ignoreStepperDivision
                maskOption="money"
                name={`equity-amount-price${id}`}
                testId={`equity-amount-price${id}`}
                defaultValue={priceItem || 0.01}
                onChange={handleChangePriceItem}
                showErrorMessage={false}
                disabled={isSelected || (!isBatchTicket && orderType === 'Market')}
                {...((totalPrice === 0 || totalPrice > 1000000) && {
                  customError: 'Valor inválido',
                })}
              />
            </Stack>
          </VStack>
        </Stack>
        <Stack w="1/5">
          <VStack space={Spacing['stack.quarck']} justifyContent="space-between">
            <UI.Text color="neutral.black" variant="semibold">
              Total estimado
            </UI.Text>
            <TableContentItemText>{Utils.Mask.Money(totalPrice)}</TableContentItemText>
          </VStack>
        </Stack>
        <Stack w="1/5">
          <Box w="100%">
            <Button
              id={selectedTab.typeGetterId}
              offer={completeOffer}
              isDisabled={totalPriceIsInvalid}
              productContextConstant={selectedTab}
            />
          </Box>
        </Stack>
      </HStack>
    </TableContent>
  );
};
